import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { PasswordInput } from '@/shared/ui/password-input'
import { Label } from '@/shared/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card'
import { useAuthStore } from '@/shared/store/authStore'
import { identityOnlyDestination, takeIntendedPath } from '@/features/auth/postLoginRedirect'
import { apiClient } from '@/api/client'
import { publicClient } from '@/api/publicClient'
import { Fingerprint } from 'lucide-react'
import { MfaChallenge } from '@/features/auth/components/MfaChallenge'
import { OrgSelector } from '@/features/auth/components/OrgSelector'
import { rememberLastOrg } from '@/features/auth/lastOrg'
import { takeSessionEndNotice } from '@/features/auth/sessionEndNotice'
import type { LoginResponsePayload, OrgOption } from '@/features/auth/types'
import { errorToUserMessage, mapAxiosError, retryAfterMinutes } from '@/api/errors'

// ── WebAuthn helpers ─────────────────────────────────────────────────────────

function base64UrlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4))
  const binary = atob(base64 + pad)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

function bufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// ── Login Page ───────────────────────────────────────────────────────────────

export function LoginPage() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [passkeyLoading, setPasskeyLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  /**
   * IN-116 — why this page is showing, when it is showing for a reason the person
   * deserves to be told. Read ONCE, in a lazy initializer: `takeSessionEndNotice`
   * clears as it reads, so the sentence appears on the sign-in that follows the access
   * change and never again — not on a reload, not on tomorrow's ordinary sign-in.
   */
  const [sessionEndNotice] = useState(takeSessionEndNotice)

  // Pending MFA challenge (null = credentials step, non-null = challenge step)
  const [mfaToken, setMfaToken] = useState<string | null>(null)
  const [mfaMethods, setMfaMethods] = useState<string[]>([])

  // Pending org selection — an identity with ≥2 active memberships (slice 2's
  // `selectorRequired` response). No session exists yet at this point: the
  // selector ticket is the only thing that can mint one.
  const [selectorToken, setSelectorToken] = useState<string | null>(null)
  const [selectorOrgs, setSelectorOrgs] = useState<OrgOption[]>([])
  const [selectingOrg, setSelectingOrg] = useState(false)

  // Track if we're in "email not confirmed" state
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false)

  const { setAuth, setIdentitySession } = useAuthStore()
  const navigate = useNavigate()

  /**
   * F90 — the cooldown sentence, with the wait the server actually reported.
   * Falls back to a timeless variant when the refusal carried no retry time (an
   * older backend, a proxy that rewrote the body): a missing number must read as
   * "later", never as "NaN minutes".
   */
  const cooldownMessage = (err: unknown): string => {
    const minutes = retryAfterMinutes(mapAxiosError(err))
    return minutes === undefined
      ? t('auth.login.tooManyAttempts')
      : t('auth.login.tooManyAttemptsIn', { count: minutes })
  }

  /**
   * The single place a login-shaped response is interpreted. Password login,
   * passkey login, post-MFA verification and org selection all land here,
   * because any of them can come back as a challenge rather than a session:
   * the second factor is proven before the membership fan-out (slice 3), so
   * "MFA, then choose a studio" is a real sequence, not a theoretical one.
   */
  function handleAuthResponse(data: LoginResponsePayload) {
    // `?? null` on both tickets: a challenge flag without its ticket is a
    // response the server cannot actually produce, and rendering the sign-in
    // form again beats rendering a challenge screen with no way to answer it.
    if (data?.mfaRequired) {
      setSelectorToken(null)
      setMfaToken(data.mfaToken ?? null)
      setMfaMethods(data.mfaMethods || [])
      return
    }
    if (data?.selectorRequired) {
      setMfaToken(null)
      setSelectorOrgs(data.orgs || [])
      setSelectorToken(data.selectorToken ?? null)
      return
    }
    completeLogin(data)
  }

  function completeLogin(data: LoginResponsePayload) {
    if (!data.accessToken) {
      // Neither a session nor a challenge. Unreachable with this backend, but
      // the old code called setAuth(undefined, undefined) here and left the
      // store holding a broken session — say something instead.
      setError(t('errors.unknown'))
      return
    }
    // The deep link they were following, if any — set by ProtectedRoute for the
    // org tree, or by /create-studio for the identity-only one.
    const intended = takeIntendedPath()

    if (!data.member) {
      // A proven identity with zero active memberships: the token carries no
      // org claim and reaches only the identity-only surface. That surface is
      // two pages wide, so a deep link is honoured only when it names one of
      // them — following /projects/42 here would just collect 403s. Otherwise
      // the "not part of any studio yet" page IS the destination.
      setIdentitySession(data.accessToken)
      navigate(identityOnlyDestination(intended))
      return
    }
    // Refresh token is delivered as an HttpOnly cookie by the server —
    // nothing to stash on the client.
    rememberLastOrg(data.member.orgSlug)
    setAuth(data.accessToken, data.member)
    // Land on the index route, never on a named page: `HomeRedirect` is mounted
    // there and is the only thing that reads the member's `home_page` preference
    // (falling back to ROLE_DEFAULTS). Hard-navigating to '/dashboard' here meant
    // that decision never ran on login — a configured home page was silently
    // ignored for every role, and a BOOKKEEPER, who IS allowed on /dashboard,
    // landed there instead of /finance. Roles that are NOT allowed on /dashboard
    // only looked correct because RoleGuard bounced them to '/' and HomeRedirect
    // rescued them on the second hop.
    navigate(intended ?? '/')
  }

  const attemptPasskeyLogin = async (): Promise<boolean> => {
    try {
      const { data: options } = await apiClient.post('/auth/passkey/login/options', {})

      const publicKey: PublicKeyCredentialRequestOptions = {
        ...options,
        challenge: base64UrlToBuffer(options.challenge),
        allowCredentials: (options.allowCredentials || []).map((c: any) => ({
          ...c,
          id: base64UrlToBuffer(c.id),
        })),
      }

      const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential
      if (!credential) return false
      const response = credential.response as AuthenticatorAssertionResponse

      const { data } = await apiClient.post('/auth/passkey/login/verify', {
        credentialId: bufferToBase64Url(credential.rawId),
        clientDataJson: bufferToBase64Url(response.clientDataJSON),
        authenticatorData: bufferToBase64Url(response.authenticatorData),
        signature: bufferToBase64Url(response.signature),
      })
      handleAuthResponse(data)
      return true
    } catch {
      return false
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setEmailNotConfirmed(false)
    setLoading(true)
    try {
      const { data } = await apiClient.post('/auth/login', { email, password })
      handleAuthResponse(data)
    } catch (err: any) {
      const msg = err?.response?.data?.message || ''
      if (msg === 'ACCOUNT_DEACTIVATED') {
        setError(t('auth.login.accountDeactivated'))
      } else if (msg.toLowerCase().includes('confirm') && msg.toLowerCase().includes('email')) {
        setEmailNotConfirmed(true)
        setError(null)
      } else if (err?.response?.data?.code === 'LOGIN_COOLDOWN') {
        // Say what the SERVER said. This replaces a counter kept in component
        // state, which was reset by every reload, tab reopen and coming-back-
        // later — so the case it existed for reproduced constantly (harness F3).
        // The cooldown is now reachable only after a genuinely wrong password,
        // so the sentence can be honest about what is happening.
        //
        // F90: and honest about HOW LONG. The copy said "a few minutes" against a
        // 30-minute lockout, so anyone who believed it came back too early to the
        // very same sentence. The server sends the time remaining; a hard-coded
        // "30 minutes" here would just be a fresh lie one minute later.
        setError(cooldownMessage(err))
      } else {
        setError(t('auth.login.invalidCredentials'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResendConfirmation = async () => {
    setResendLoading(true)
    setError(null)
    try {
      await publicClient.post('/auth/resend-confirmation', { email })
      setResendSuccess(true)
    } catch (err: any) {
      setError(errorToUserMessage(err, t))
    } finally {
      setResendLoading(false)
    }
  }

  const backToCredentials = () => {
    setSelectorToken(null)
    setSelectorOrgs([])
    setMfaToken(null)
    setMfaMethods([])
    setPassword('')
    setError(null)
  }

  const handleOrgSelect = async (slug: string) => {
    if (!selectorToken) return
    setSelectingOrg(true)
    setError(null)
    try {
      const { data } = await apiClient.post('/auth/select-org', {
        selectorToken,
        orgSlug: slug,
      })
      handleAuthResponse(data)
    } catch (err) {
      // The ticket is single-use and the server spends it on read, so a retry
      // of the same click can only fail again. Drop back to the credentials
      // step with an honest message rather than leaving a dead list of buttons
      // in front of a non-technical user.
      backToCredentials()
      setError(errorToUserMessage(err, t))
    } finally {
      setSelectingOrg(false)
    }
  }

  const handlePasskeyLogin = async () => {
    setError(null)
    setPasskeyLoading(true)
    const success = await attemptPasskeyLogin()
    setPasskeyLoading(false)
    if (!success) setError(t('auth.login.passkeyFailed'))
  }

  const passkeySupported = typeof window !== 'undefined' && !!window.PublicKeyCredential
  const anyLoading = loading || googleLoading || passkeyLoading

  // ── MFA Challenge ─────────────────────────────────────────────────────────

  if (mfaToken) {
    return (
      <MfaChallenge
        mfaToken={mfaToken}
        mfaMethods={mfaMethods}
        onSuccess={handleAuthResponse}
        onCancel={backToCredentials}
      />
    )
  }

  // ── Studio Selector ───────────────────────────────────────────────────────
  //
  // Identity proven, ≥2 active memberships, no session yet.

  if (selectorToken) {
    return (
      <OrgSelector
        orgs={selectorOrgs}
        busy={selectingOrg}
        error={error}
        onSelect={handleOrgSelect}
        footer={
          <Button
            type="button"
            variant="ghost"
            className="w-full"
            disabled={selectingOrg}
            onClick={backToCredentials}
          >
            {t('auth.orgSelector.back')}
          </Button>
        }
      />
    )
  }

  // ── Credentials Step ──────────────────────────────────────────────────────

  if (emailNotConfirmed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t('auth.brand')}</CardTitle>
            <p className="text-sm text-muted-foreground">{t('auth.confirm.tagline')}</p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground text-center">
                <Trans
                  i18nKey="auth.confirm.body"
                  values={{ email }}
                  components={{ strong: <strong /> }}
                />
              </p>

              {error && <p className="text-sm text-destructive text-center">{error}</p>}

              {resendSuccess && (
                <p className="text-sm text-green-600 bg-green-50 p-2 rounded text-center">
                  {t('auth.confirm.resendSuccess')}
                </p>
              )}

              <Button
                onClick={handleResendConfirmation}
                disabled={resendLoading || resendSuccess}
                variant="outline"
                className="w-full"
              >
                {resendLoading
                  ? t('auth.confirm.resendSending')
                  : resendSuccess
                    ? t('auth.confirm.resendSent')
                    : t('auth.confirm.resendButton')}
              </Button>

              <Button
                onClick={() => {
                  setEmailNotConfirmed(false)
                  setResendSuccess(false)
                  setError(null)
                }}
                variant="ghost"
                className="w-full"
              >
                {t('auth.confirm.back')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">{t('auth.brand')}</CardTitle>
          <p className="text-sm text-muted-foreground">{t('auth.login.tagline')}</p>
          {sessionEndNotice && (
            <p role="status" className="mt-3 rounded-md bg-muted px-3 py-2 text-sm text-foreground">
              {sessionEndNotice.reason === 'EXPIRED'
                ? t('auth.login.sessionEndedNotice')
                : sessionEndNotice.studio
                  ? t('auth.login.accessChangedNotice', { studio: sessionEndNotice.studio })
                  : t('auth.login.accessChangedNoticeNoStudio')}
            </p>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">{t('auth.login.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="username webauthn"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t('auth.login.passwordLabel')}</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-muted-foreground hover:underline"
                >
                  {t('auth.login.forgotPassword')}
                </Link>
              </div>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                revealed={showPassword}
                onRevealChange={setShowPassword}
                showLabel={t('auth.login.showPassword')}
                hideLabel={t('auth.login.hidePassword')}
              />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button
              type="submit"
              variant="outline"
              disabled={anyLoading}
              className="w-full"
            >
              {loading ? t('auth.login.submitLoading') : t('auth.login.submit')}
            </Button>

            <div className="relative my-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">{t('auth.login.or')}</span>
              </div>
            </div>

            {passkeySupported && (
              <Button
                type="button"
                className="w-full gap-2 bg-slate-900 text-white hover:bg-slate-800"
                disabled={anyLoading}
                onClick={handlePasskeyLogin}
              >
                <Fingerprint size={16} />
                {passkeyLoading ? t('auth.login.passkeyWaiting') : t('auth.login.passkey')}
              </Button>
            )}

            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              disabled={anyLoading}
              onClick={async () => {
                setGoogleLoading(true)
                setError(null)
                try {
                  const { data } = await apiClient.get('/auth/oauth/google/authorize')
                  window.location.href = data.url
                } catch {
                  setError(t('auth.login.googleFailed'))
                  setGoogleLoading(false)
                }
              }}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {googleLoading ? t('auth.login.googleRedirecting') : t('auth.login.google')}
            </Button>

            <p className="text-sm text-center text-muted-foreground">
              {t('auth.login.inviteHint')}
            </p>
            <p className="text-sm text-center text-muted-foreground">
              {t('auth.login.registerPrompt')}{' '}
              <Link to="/register" className="text-primary hover:underline font-medium">
                {t('auth.login.registerCta')}
              </Link>
            </p>
            <p className="text-xs text-center text-muted-foreground">
              <Link to="/guide" className="hover:underline">
                {t('auth.login.userGuide')}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}